import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { H, MANROPE, FOCUS } from "../components/healthcheck/hcTokens";
import HealthCheckForm from "../components/healthcheck/HealthCheckForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/health-check`;
const pendingKey = (t) => `equipr-hc-pending-${t}`;

const Shell = ({ children, wide }) => (
  <div className="min-h-screen w-full" style={{ background: H.page }} data-testid="hc-public">
    <div className="h-[54px] flex items-center px-5" style={{ background: "#121212" }}>
      <div style={{ ...MANROPE, fontSize: 15, fontWeight: 800, letterSpacing: "0.02em", color: "#fff" }}>
        EQUIPR
      </div>
      <div className="ml-3 text-[11px] font-bold uppercase px-2 py-0.5 rounded"
        style={{ background: "#FFD108", color: "#111", letterSpacing: "0.1em" }}>Health Check</div>
    </div>
    <div className="mx-auto px-5 py-10 sm:py-14" style={{ maxWidth: wide ? 760 : 640 }}>{children}</div>
  </div>
);

const Card = ({ children }) => (
  <div className="rounded-[14px] px-5 py-7 sm:px-9 sm:py-10"
    style={{ background: H.card, border: `1px solid ${H.border}` }}>{children}</div>
);

const Title = ({ children, sub }) => (
  <div className="mb-7">
    <h1 style={{ ...MANROPE, fontSize: 27, fontWeight: 800, letterSpacing: "-0.02em", color: H.ink }}>{children}</h1>
    {sub && <div className="text-[15px] mt-2" style={{ color: H.ink2 }}>{sub}</div>}
  </div>
);

const EntryPage = () => {
  const [id, setId] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const check = async () => {
    const v = id.trim().toUpperCase();
    if (!v) return;
    setBusy(true); setErr("");
    try {
      await axios.get(`${API}/lookup/${encodeURIComponent(v)}`);
      setErr("found");
    } catch {
      setErr("We couldn't find your account. Please check the ID or contact us.");
    } finally { setBusy(false); }
  };
  return (
    <Shell>
      <Card>
        <Title sub="Health Check links are personal, so we can't open the form from an ID alone. Enter your EQUIPR Client ID and we'll tell you whether we have you on file.">
          Your EQUIPR Client ID
        </Title>
        <input value={id} onChange={(e) => setId(e.target.value)} placeholder="For example EAG123"
          data-testid="hc-entry-id" className={`w-full h-12 rounded-[9px] px-3 text-[16px] bg-white ${FOCUS}`}
          style={{ border: `1px solid ${H.control}`, color: H.ink }} />
        {err && err !== "found" && (
          <div className="mt-4 text-[14px]" data-testid="hc-entry-error" style={{ color: "#8E1212" }}>{err}</div>
        )}
        {err === "found" && (
          <div className="mt-4 text-[14px]" data-testid="hc-entry-found" style={{ color: H.ink }}>
            We have you on file. Ask your EQUIPR contact to resend your personal Health Check link.
          </div>
        )}
        <button onClick={check} disabled={busy} data-testid="hc-entry-submit"
          className={`mt-6 h-12 px-6 rounded-[9px] text-[14px] font-bold uppercase ${FOCUS}`}
          style={{ background: H.accent, border: `1px solid ${H.accentEdge}`, color: "#111", letterSpacing: "0.05em" }}>
          {busy ? "Checking…" : "Continue"}
        </button>
      </Card>
    </Shell>
  );
};

export default function HealthCheckPublicPage() {
  const { clientId } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("t") || "";
  const staffMode = sp.get("mode") === "staff";

  const [state, setState] = useState("loading");   // loading|form|done|not_found|denied|error
  const [form, setForm] = useState(null);
  const [a, setA] = useState({});
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [problems, setProblems] = useState([]);
  const [retrying, setRetrying] = useState(false);
  const dirty = useRef(false);

  useEffect(() => {
    if (staffMode && clientId) nav(`/health-check/${clientId}/staff`, { replace: true });
  }, [staffMode, clientId, nav]);

  const submit = useCallback(async (answers, attempt = 0) => {
    try {
      const r = await axios.post(`${API}/public/${encodeURIComponent(clientId)}/submit?t=${token}`,
        { answers });
      localStorage.removeItem(pendingKey(token));
      setRetrying(false);
      setForm((f) => ({ ...(f || {}), thank_you: r.data.thank_you }));
      setState("done");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400) { setProblems([e.response.data?.detail || "Please check your answers."]); return; }
      if (status === 403) { setState("denied"); return; }
      // Nothing is lost: hold the answers and keep trying.
      localStorage.setItem(pendingKey(token), JSON.stringify(answers));
      setRetrying(true);
      if (attempt < 4) {
        setTimeout(() => submit(answers, attempt + 1), 2000 * (attempt + 1));
      }
    } finally { setBusy(false); }
  }, [clientId, token]);

  useEffect(() => {
    if (!clientId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/${encodeURIComponent(clientId)}?t=${token}`);
        if (r.data.already_submitted) { setForm(r.data); setState("done"); return; }
        setForm(r.data);
        const held = localStorage.getItem(pendingKey(token));
        const start = { ...(r.data.prefill || {}), ...(r.data.answers || {}) };
        if (held) {
          const answers = JSON.parse(held);
          setA(answers); setState("form"); setBusy(true); submit(answers);
          return;
        }
        setA(start);
        setState("form");
      } catch (e) {
        const s = e?.response?.status;
        setState(s === 404 ? "not_found" : s === 403 ? "denied" : "error");
      }
    })();
  }, [clientId, token, submit]);

  /* Save and resume — the link remembers where they got to. */
  useEffect(() => {
    if (state !== "form" || !token) return;
    if (!dirty.current) { dirty.current = true; return; }
    const id = setTimeout(async () => {
      try {
        await axios.put(`${API}/public/${encodeURIComponent(clientId)}/draft?t=${token}`, { answers: a });
        setSavedAt(new Date().toISOString());
      } catch { /* a failed autosave is not worth telling the client about */ }
    }, 1200);
    return () => clearTimeout(id);
  }, [a, state, clientId, token]);

  if (!clientId) return <EntryPage />;

  if (state === "loading") {
    return <Shell><Card><div className="text-[15px]" style={{ color: H.ink2 }}>Loading your Health Check…</div></Card></Shell>;
  }

  if (state === "not_found") {
    return (
      <Shell><Card>
        <Title sub="Please check the link in your email, or contact us and we'll sort it out.">
          We couldn't find your account
        </Title>
        <div className="text-[15px]" data-testid="hc-not-found" style={{ color: H.ink }}>
          Contact us at{" "}
          <a href="mailto:support@equipr.software" style={{ color: H.accentInk, fontWeight: 700 }}>support@equipr.software</a>.
        </div>
      </Card></Shell>
    );
  }

  if (state === "denied") {
    return (
      <Shell><Card>
        <Title sub="Health Check links are personal and expire after 30 days.">
          This link isn't valid any more
        </Title>
        <div className="text-[15px]" data-testid="hc-denied" style={{ color: H.ink }}>
          Ask your EQUIPR contact to send you a fresh link and we'll pick up where you left off.
        </div>
      </Card></Shell>
    );
  }

  if (state === "error") {
    return <Shell><Card><Title>Something went wrong</Title>
      <div className="text-[15px]" style={{ color: H.ink }}>Please try the link again in a moment.</div>
    </Card></Shell>;
  }

  if (state === "done") {
    return (
      <Shell><Card>
        <Title>Thank you</Title>
        <div className="text-[16px] leading-relaxed" data-testid="hc-thanks" style={{ color: H.ink }}>
          {(form?.thank_you) ||
            "Your feedback has gone straight to our team. We'll come back to you on everything you've raised within 30 days."}
        </div>
      </Card></Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-6">
        <h1 style={{ ...MANROPE, fontSize: 27, fontWeight: 800, letterSpacing: "-0.02em", color: H.ink }}>
          Your EQUIPR Health Check
        </h1>
        <div className="text-[15px] mt-2" style={{ color: H.ink2 }}>
          About five minutes. It isn't a support call and it isn't a sales call — we just want to
          hear how we're doing and what's getting in your way.
        </div>
      </div>
      {form.test && (
        <div className="rounded-[9px] px-4 py-3 mb-4 text-[13.5px]" data-testid="hc-test-banner"
          style={{ background: "#E3F3E9", border: "1px solid #A9D9BE", color: "#0B4F2E" }}>
          <b>Test link.</b> This is exactly what the client sees, and nothing you submit here is
          recorded against them.
        </div>
      )}
      {retrying && (        <div className="rounded-[9px] px-4 py-3 mb-4 text-[13.5px]" data-testid="hc-retrying"
          style={{ background: "#FFF7D1", border: "1px solid #E8D48A", color: "#6B5600" }}>
          We're still sending your answers. They're saved on this device, so nothing is lost — keep
          this page open a moment longer.
        </div>
      )}
      <Card>
        <HealthCheckForm mode="client" clientId={form.client_id} companyName={form.company_name}
          meta={form.meta} a={a} setA={setA} savedAt={savedAt} problems={problems} busy={busy}
          onSubmit={() => { setProblems([]); setBusy(true); submit(a); }} />
      </Card>
      <div className="text-[12.5px] mt-5" style={{ color: H.ink3 }}>
        EQUIPR Software · Your answers are held securely and used to improve our software and service.
      </div>
    </Shell>
  );
}
